/**
 * v1.6.1814 — per-tenant export switches (Asana 1219882772481516).
 *
 * The platform team can now switch a tenant's exports off. Two switches,
 * read from `GET /v1/tenant/info`:
 *
 *   is_general_export_enabled   every DATA export (products, gift cards,
 *                               analytics, rewards, scans, campaign results,
 *                               the meta feed, export-run downloads)
 *   is_report_export_enabled    creating a report, and the CSV / PDF / HTML
 *                               buttons on the reports list
 *
 * ── The three traps ─────────────────────────────────────────────────────
 *
 * 1. A SWITCH 403 IS NOT A PERMISSION 403.
 *    A refused call answers 403 with `data.switch` set to "general" or
 *    "reports". The permission interceptor claims every 403 that is not a
 *    billing block, so without an explicit exclusion the operator is told to
 *    "ask an administrator to add the missing permission to your role" —
 *    advice no administrator can act on, because no permission is missing.
 *    This is the same shape as the billing 403, which is told apart by
 *    `data.reason`; `data.switch` is told apart the same way.
 *
 * 2. ABSENT IS NOT OFF.
 *    An older backend, or any read that has not resolved yet, has no flag at
 *    all. Treating a missing flag as "off" would hide every export button in
 *    the panel on a tenant with exports perfectly enabled. Only an explicit
 *    `false` switches anything off.
 *
 * 3. PRINTOUTS AND IMPORT ERRORS ARE NOT EXPORTS.
 *    Labels, bag tags, bag stickers and packing slips are never gated, and
 *    the import ERROR file on the importers page keeps downloading with
 *    general exports off — it is the record of what failed, not an export of
 *    the tenant's data. Report settings and packing slip settings are not
 *    gated either.
 */

export type ExportSwitch = 'general' | 'reports';

/**
 * Trap 2 — only an explicit `false` is off.
 *
 * `undefined` (old backend, or not loaded yet) and `null` both mean "no
 * opinion", which must read as enabled.
 */
export function switchIsOff(raw: any): boolean {
  return raw === false || raw === 0 || raw === '0' || raw === 'false';
}

export function readExportSwitches(payload: any): {
  general: boolean;
  reports: boolean;
} {
  const d = payload?.data ?? payload ?? {};
  return {
    general: !switchIsOff(d.is_general_export_enabled),
    reports: !switchIsOff(d.is_report_export_enabled),
  };
}

/**
 * Trap 1 — which switch refused this call, or null if it was not a switch.
 *
 * Only the two documented values are accepted. An unrecognised `switch`
 * value is NOT claimed: better that it falls through to the ordinary 403
 * handling, which at least shows the server's message, than that it is
 * silently relabelled as something this build understands.
 */
export function readRefusedSwitch(err: any): ExportSwitch | null {
  if (Number(err?.status) !== 403) return null;
  const s = err?.error?.data?.switch;
  if (s === 'general' || s === 'reports') return s;
  return null;
}

export function isExportSwitchForbidden(err: any): boolean {
  return readRefusedSwitch(err) !== null;
}

/**
 * The message to show, as sent. The ticket says the 403 "comes with a ready
 * message"; it names who can switch exports back on, which no wording of
 * ours could.
 */
export function exportSwitchMessage(err: any): string {
  const raw = String(err?.error?.message ?? '').trim();
  if (raw) return raw;
  // Only reached if the server sends a switch 403 with no message at all.
  return readRefusedSwitch(err) === 'reports'
    ? 'Report exports are switched off for this business.'
    : 'Data exports are switched off for this business.';
}

export const EXPORTS_OFF_NOTE =
  'Exports are switched off for this business, so the export actions are hidden.';

export const REPORT_EXPORTS_OFF_NOTE =
  'Report exports are switched off for this business, so creating and downloading reports is hidden.';
